import React, { useMemo, useState } from 'react';

import CompactTabMenu from 'components/nav/CompactTabMenu/CompactTabMenu';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';
import { DRAWERTABS, DrawerTabOptions } from '../../../../constants/formula';

import {
    ApprovedFormulaState,
    FormulaDetailModel,
    FormulaSchedulingOptions,
} from 'models/formula.model';

import AuditTrailWrapper from 'views/activity-logs/AuditTrailWrapper';

import ContentTab from './ContentTab';
import SummaryTab from './SummaryTab';

interface FormulaTabsProps {
    data: FormulaDetailModel;
    timingSlots: any;
    schedulingType: FormulaSchedulingOptions;
    setShowTimeSlotsModal: React.Dispatch<React.SetStateAction<boolean>>;
    refocusGrid: () => void;
    currentTab?: DrawerTabOptions;
    onTabChange?: (tab: DrawerTabOptions) => void;
    templateName?: string;
    isCustomTemplate?: boolean;
}

const tabMenuStyling: React.CSSProperties = {
    display: 'flex',
    borderBottom: '1px solid #E0E0E0',
    background: '#FFFFFF',
};

const tabStyling = (isActive: boolean) => ({
    margin: aampeTheme.spacing4(1, 4, 0, 4),
    padding: aampeTheme.spacing4(0, 0, 2, 0),
    fontWeight: isActive ? aampeTheme.weights.heavy : aampeTheme.weights.light,
    color: '#1A1A1A',
    lineHeight: aampeTheme.size.lineHeight.small,
    alignItems: 'flex-end',
    marginRight: 0,
    backgroundColor: '#FFFFFF',
    borderBottom: isActive
        ? `2px solid ${aampeTheme.colors.orange400}`
        : '1px solid #E0E0E0 ',
    marginBottom: '-1px',
    cursor: 'pointer',
    fontSize: aampeTheme.size.font.textMedium,
});

export const FormulaTabs = ({
    data,
    timingSlots,
    schedulingType,
    setShowTimeSlotsModal,
    refocusGrid,
    currentTab,
    onTabChange,
    templateName,
    isCustomTemplate,
}: FormulaTabsProps) => {
    const [internalTab, setInternalTab] = useState<DrawerTabOptions>(
        DrawerTabOptions.summary,
    );
    const activeTab = currentTab ? currentTab : internalTab;

    const handleTabClick = (tabKey: string) => {
        if (
            !Object.values(DrawerTabOptions).includes(
                tabKey as DrawerTabOptions,
            )
        ) {
            console.warn('Invalid tab key:', tabKey);
            return;
        }

        const tab = tabKey as DrawerTabOptions;

        if (onTabChange) onTabChange(tab);
        else setInternalTab(tab);
    };
    const renderTabContent = useMemo(() => {
        switch (activeTab) {
            case DrawerTabOptions.summary:
                return (
                    <SummaryTab
                        data={data}
                        timingSlots={timingSlots}
                        schedulingType={schedulingType}
                        setShowTimeSlotsModal={setShowTimeSlotsModal}
                        templateName={templateName}
                        isCustomTemplate={isCustomTemplate}
                    />
                );
            case DrawerTabOptions.content:
                return <ContentTab data={data} />;

            case DrawerTabOptions.trail:
                return (
                    <TrailDrawer>
                        <AuditTrailWrapper
                            hideHeading
                            formulaId={data.id.toString()}
                        />
                    </TrailDrawer>
                );
            default:
                return null;
        }
    }, [
        activeTab,
        data,
        timingSlots,
        schedulingType,
        setShowTimeSlotsModal,
        templateName,
        isCustomTemplate,
    ]);

    const filteredTabs = useMemo(() => {
        return DRAWERTABS.filter((tab) => {
            if (tab.key === DrawerTabOptions.trail) {
                return data.active_state === ApprovedFormulaState.Live;
            }
            return true;
        });
    }, [data.active_state]);

    return (
        <>
            <CompactTabMenu
                activeTabKey={activeTab}
                refocusGrid={refocusGrid}
                onTabClick={handleTabClick}
                tabs={filteredTabs}
                tabMenuStyling={tabMenuStyling}
                tabStyling={tabStyling}
            />
            {renderTabContent}
        </>
    );
};

const TrailDrawer = styled.div({
    padding: aampeTheme.spacing4(5, 5, 10, 5),
});
